import { useQuery } from '@apollo/client';
import {
  Box,
  FormControl,
  Icon,
  SideSheet,
  SideSheetSize,
  Text,
  TextField,
  ToastAction,
  ToastType,
  useModalState,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  getPermissionMessage,
  hasPermissions,
  i18next as i18n,
  i18next,
  useAppUser,
  useProjectConfiguration,
} from '@bechtel/ht-clients-common';
import { AuthPermission, ProjectConfigurations, UpdateProjectError, UpdateProjectInput } from '@bechtel/ht-types';
import { Formik } from 'formik';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { ErrorModal } from '~/components/molecules/error-modal';
import { GET_PROJECT } from '~/gql/projects';
import { useUpdateProject } from '~/hooks/use-update-project';
import { PrimaryButtonStyled } from '~/styles';
import { NewProject } from './new-project/internal';
import { CancelButtonStyled, FooterStyled, GeneralContainer, HRStyled } from './styled';

const ErrorField = ({ errorMsg }) => {
  return (
    <Box pt={{ sm: '200' }} pl={{ sm: '300' }}>
      <Text size="250" color="bechtel.red">
        {errorMsg}
      </Text>
    </Box>
  );
};

export const General: FC = () => {
  const { t } = useTranslation();
  const { appUser } = useAppUser();
  const { getConfigurationValue, formattedDate, formattedDateTime } = useProjectConfiguration();
  const validationSchema = object().shape({
    thProjectNumber: string().trim().required(t('TOOLHOUND_PROJECT_NUMBER_MANDATORY')),
    projectName: string().trim().required(t('PROJECT_NAME_MANDATORY')),
    projectNumber: string().trim().required(t('PROJECT_NUMBER_MANDATORY')),
  });
  const canCreateNewProject =
    appUser?.bun.toLocaleUpperCase() == 'AKUMAR63' ||
    appUser?.bun.toLocaleUpperCase() == 'SGROVER' ||
    appUser?.bun.toLocaleUpperCase() == 'JRHARTER';
  const [hasError, setHasError] = useState<boolean>(false);
  const [redirectErrorMessage, setRedirectErrorMessage] = useState('');
  const dispatchToast = useToastsDispatch();
  const locationConfig: any = getConfigurationValue(ProjectConfigurations.GEO_LOCATION);
  const { updateProject } = useUpdateProject();
  //TODO: for now the value of refetchData is false, later we change to ture. if we you want use refetch(), change the refetchData to true if you want refetch()
  const { data, loading, error, refetch } = useQuery(GET_PROJECT, {
    variables: { projectId: appUser?.defaultProject?.id },
  });
  const { open, close, visible } = useModalState(false);
  const [setProjectData, setInitialValues] = useState({
    id: '',
    isActive: false,
    thParentLocationId: '',
    projectName: '',
    projectNumber: '',
    subJobNumber: '',
  });

  useEffect(() => {
    if (data?.project) {
      setInitialValues({
        id: data.project.id,
        isActive: data.project.isActive,
        thParentLocationId: data.project.thParentLocationId || '',
        projectName: data.project.name,
        projectNumber: data.project.jobNumber,
        subJobNumber: data.project.subJobNumber,
      });
    }
  }, [data]);

  const closeModal = () => {
    if (hasError) {
      setRedirectErrorMessage('');
      setHasError(false);
      refetch();
    }
  };
  return (
    <GeneralContainer>
      <Box css={{ height: '90vh' }}>
        {redirectErrorMessage && (
          <ErrorModal
            close={closeModal}
            message={redirectErrorMessage}
            title={i18n.t('UPDATE_PROJECT')}
            closeText={i18n.t('OK')}
          />
        )}
        <Formik
          initialValues={{
            id: setProjectData?.id,
            isActive: setProjectData?.isActive,
            thProjectNumber: setProjectData?.thParentLocationId,
            projectName: setProjectData?.projectName,
            projectNumber: setProjectData?.projectNumber,
            subJobNumber: setProjectData?.subJobNumber,
            language: 'English',
            dateFormat: formattedDate(new Date()),
            dateTimeFormat: formattedDateTime(new Date()),
            latitude: locationConfig?.latitude,
            longitude: locationConfig?.longitude,
            elevation: locationConfig?.elevation,
          }}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={async (values) => {
            const updateProjectInput: UpdateProjectInput = {
              isActive: values.isActive ?? false,
              thParentLocationId: values.thProjectNumber,
              subJobNumber: values.subJobNumber ?? '',
              jobNumber: values.projectNumber ?? '',
              name: values.projectName ?? '',
              id: values.id ?? '',
            };
            updateProject(
              updateProjectInput,
              (data) => {
                if (data?.error) {
                  const rolePermissionToast: ToastAction = {
                    type: 'ADD_TOAST',
                    payload: {
                      toastType: ToastType.DANGER,
                      content:
                        data?.error === UpdateProjectError.PROJECT_NOT_FOUND ||
                        data?.error === UpdateProjectError.JOB_NUMBER_ALREADY_EXISTS
                          ? i18next.t(data?.error)
                          : i18next.t('COMMON_ERROR_MESSAGE'),
                      autoDismissTimeout: 3000,
                    },
                  };
                  dispatchToast(rolePermissionToast);
                } else {
                  setHasError(true);
                  setRedirectErrorMessage(i18n.t('PROJECT_UPDATE_SUCCESS_REDIRECT'));
                }
              },
              (errors) => {
                const updateProjectToast: ToastAction = {
                  type: 'ADD_TOAST',
                  payload: {
                    toastType: ToastType.DANGER,
                    content: i18n.t(getPermissionMessage(errors.message)),
                    autoDismissTimeout: 3000,
                  },
                };
                dispatchToast(updateProjectToast);
              }
            );
          }}
        >
          {({ values, handleChange, handleBlur, handleSubmit, dirty, isValid, touched, resetForm, errors }) => {
            return (
              <>
                <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }} justifyContent={{ sm: 'space-between' }}>
                  <form style={{ width: '100%', overflowY: 'auto' }}>
                    <Box mb={{ sm: '700' }} mt={{ sm: '500' }} px={{ sm: '500' }}>
                      <Text family="openSansSemiBold" weight="600" size="500" color="neutral.800">
                        {t('PROJECT_OVERVIEW')}
                      </Text>
                    </Box>
                    <Box
                      flexWrap={{ sm: 'wrap' }}
                      justifyContent={{ sm: 'flex-start' }}
                      css={{ width: '70%' }}
                      px={{ sm: '500' }}
                    >
                      <Box pr={{ sm: '500' }} pb={{ sm: '700' }} flexBasis={{ sm: '50%' }}>
                        <FormControl label={t('TOOLHOUND_PROJECT_LOCATION')} css={{ width: '100%' }}>
                          <TextField
                            name="thProjectNumber"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.thProjectNumber || ''}
                            disabled={!hasPermissions([AuthPermission.EDIT_PROJECT], appUser)}
                          />
                          <ErrorField errorMsg={errors.thProjectNumber} />
                        </FormControl>
                      </Box>
                      <Box pb={{ sm: '700' }} flexBasis={{ sm: '50%' }}>
                        <FormControl label={t('PROJECT_NAME')} css={{ width: '100%' }}>
                          <TextField
                            name="projectName"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.projectName || ''}
                            disabled={!hasPermissions([AuthPermission.EDIT_PROJECT], appUser)}
                          />
                          <ErrorField errorMsg={errors.projectName} />
                        </FormControl>
                      </Box>
                      <Box pr={{ sm: '500' }} pb={{ sm: '700' }} flexBasis={{ sm: '50%' }}>
                        <FormControl label={t('PROJECT_NUMBER')} css={{ width: '100%' }}>
                          <TextField
                            name="projectNumber"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.projectNumber || ''}
                            disabled={!hasPermissions([AuthPermission.EDIT_PROJECT], appUser)}
                          />
                          <ErrorField errorMsg={errors.projectNumber} />
                        </FormControl>
                      </Box>
                      <Box pr={{ sm: '500' }} pb={{ sm: '700' }} flexBasis={{ sm: '50%' }}>
                        <FormControl label={i18n.t('SUB_JOB_NUMBER')} css={{ width: '100%' }}>
                          <TextField
                            name="subJobNumber"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.subJobNumber || ''}
                            disabled={!hasPermissions([AuthPermission.EDIT_PROJECT], appUser)}
                          />
                        </FormControl>
                      </Box>
                    </Box>

                    <HRStyled />

                    <Box mb={{ sm: '700' }} px={{ sm: '500' }}>
                      <Text family="openSansSemiBold" weight="600" size="500" color="neutral.800">
                        {t('LANGUAGE_AND_REGION')}
                      </Text>
                    </Box>
                    <Box
                      flexWrap={{ sm: 'wrap' }}
                      justifyContent={{ sm: 'flex-start' }}
                      css={{ width: '70%' }}
                      px={{ sm: '500' }}
                    >
                      <Box pr={{ sm: '500' }} pb={{ sm: '700' }} flexBasis={{ sm: '50%' }}>
                        <FormControl label={t('LANGUAGE')} css={{ width: '100%' }}>
                          <TextField
                            name="language"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.language}
                            disabled={true}
                          />
                        </FormControl>
                      </Box>
                    </Box>

                    <HRStyled />

                    <Box mb={{ sm: '700' }} px={{ sm: '500' }}>
                      <Text family="openSansSemiBold" weight="600" size="500" color="neutral.800">
                        {t('PROJECT_LOCATION')}
                      </Text>
                    </Box>
                    <Box
                      flexWrap={{ sm: 'wrap' }}
                      justifyContent={{ sm: 'flex-start' }}
                      css={{ width: '70%' }}
                      px={{ sm: '500' }}
                    >
                      <Box pr={{ sm: '500' }} pb={{ sm: '700' }} flexBasis={{ sm: '50%' }}>
                        <FormControl label={t('LATITUDE')} css={{ width: '100%' }}>
                          <TextField
                            name="latitude"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.latitude}
                            disabled={true}
                          />
                        </FormControl>
                      </Box>
                      <Box pb={{ sm: '700' }} flexBasis={{ sm: '50%' }}>
                        <FormControl label={t('LONGITUDE')} css={{ width: '100%' }}>
                          <TextField
                            name="longitude"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.longitude}
                            disabled={true}
                          />
                        </FormControl>
                      </Box>
                      <Box pr={{ sm: '500' }} pb={{ sm: '700' }} flexBasis={{ sm: '50%' }}>
                        <FormControl label={t('ELEVATION')} css={{ width: '100%' }}>
                          <TextField
                            name="elevation"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.elevation}
                            disabled={true}
                          />
                        </FormControl>
                      </Box>
                    </Box>

                    <HRStyled />

                    <Box mb={{ sm: '700' }} px={{ sm: '500' }}>
                      <Text family="openSansSemiBold" weight="600" size="500" color="neutral.800">
                        {t('DATE_AND_TIME')}
                      </Text>
                    </Box>
                    <Box
                      flexWrap={{ sm: 'wrap' }}
                      justifyContent={{ sm: 'flex-start' }}
                      flexDirection={{ sm: 'column' }}
                      css={{ width: '70%' }}
                      px={{ sm: '500' }}
                    >
                      <Box pr={{ sm: '500' }} pb={{ sm: '700' }} css={{ width: '50%' }}>
                        <FormControl label={t('DATE_FORMAT')} css={{ width: '100%' }}>
                          <TextField
                            name="dateFormat"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.dateFormat}
                            disabled={true}
                          />
                        </FormControl>
                      </Box>

                      <Box pr={{ sm: '500' }} pb={{ sm: '700' }} css={{ width: '50%' }}>
                        <FormControl label={t('DATETIME_FORMAT')} css={{ width: '100%' }}>
                          <TextField
                            name="dateTimeFormat"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.dateTimeFormat}
                            disabled={true}
                          />
                        </FormControl>
                      </Box>
                    </Box>
                  </form>
                  <FooterStyled>
                    {canCreateNewProject && hasPermissions([AuthPermission.ADD_PROJECT], appUser) ? (
                      <PrimaryButtonStyled type="button" onClick={() => open()}>
                        <Box gap={{ sm: '400' }}>
                          <Icon icon="plus" fillColor="white" iconStyle="regular"></Icon>
                          <Text color="white" size="300">
                            {t('New_Project')}
                          </Text>
                        </Box>
                      </PrimaryButtonStyled>
                    ) : (
                      <Box></Box>
                    )}

                    <Box gap={{ sm: '400' }}>
                      <CancelButtonStyled onClick={() => resetForm()} disabled={!touched || !dirty || !isValid}>
                        <Text color="grey.1000" size="300">
                          {t('CANCEL')}
                        </Text>
                      </CancelButtonStyled>
                      <PrimaryButtonStyled
                        type="button"
                        onClick={() => handleSubmit()}
                        disabled={!touched || !dirty || !isValid}
                      >
                        <Text color="white" size="300">
                          {t('Save_Changes')}
                        </Text>
                      </PrimaryButtonStyled>
                    </Box>
                  </FooterStyled>
                </Box>
              </>
            );
          }}
        </Formik>
      </Box>
      <SideSheet visible={visible} size={SideSheetSize.DEFAULT}>
        <NewProject onClose={close}></NewProject>
      </SideSheet>
    </GeneralContainer>
  );
};
